import { useAuth } from './../../context/authContext';
import { getToken } from './../../api/auth';
import { useEffect, useRef, useState } from 'react';
import "./Login.css";
import { useLoading } from '../../context/loadingContext';
import { setBaseAPIToken } from '../../api/api.base';
import { useDevice } from '../../context/deviceContext';
import * as Association from '../../api/association';

interface Association {
    id: number;
    name: string;
    icon?: string;
}


const Login = () => {
    const defaultAssociationIcon = 'https://assets.sportifyapp.co/images/CompactSportify.png';
    const { login, isAuthenticated, changeAssociation, validatingSession } = useAuth();
    const { deviceUUID, validatingDevice } = useDevice();
    const { setLoadingStatus, loading } = useLoading();
    const [association, setAssociation] = useState<number | null>(null);
    const [associations, setAssociations] = useState<Association[]>([]);
    const [isReadyToLogin, setIsReadyToLogin] = useState<boolean>(false);
    const [urlToken, setUrlToken] = useState<string>("");
    const [accessToken, setAccessToken] = useState<string>("");
    const [refreshToken, setRefreshToken] = useState<string>("");
    const [noAssociation, setNoAssociation] = useState<boolean | null>(null);
    const requestedUrlTokenRef = useRef<string>("");

    // Remove local loading, use only global loading

    const loadAssociations = async () => {
        const res = await Association.list();
        const associationsData = res.data;
        if (associationsData.length === 0) {
            setNoAssociation(true);
        } else {
            setNoAssociation(false);
            setAssociations(associationsData);
        }
    };

    const redirectToAuth = () => {
        if (!deviceUUID) {
            return;
        }
        const redirect = window.location.hostname.includes('localhost')
            ? 'local'
            : window.location.hostname.includes('dashboard-dev.sportifyapp.co')
                ? 'dev'
                : 'dashboard';
        window.location.href = 'https://auth.sportifyapp.co' + '?redirect=' + redirect + "&type=web&device=" + deviceUUID;
    };

    useEffect(() => {        
        setLoadingStatus(true, false);
        // Step 1: If still validating device/session, wait
        if (validatingDevice || validatingSession) return;

        // Step 2: If already authenticated, load associations
        if (isAuthenticated) {
            loadAssociations().finally(() => setLoadingStatus(false));
            return;
        }

        // Step 3: Not authenticated, check for token
        const params = new URLSearchParams(window.location.search);
        const tokenFromUrl = params.get('token');

        if (!tokenFromUrl) {
            // No token, redirect to auth
            setLoadingStatus(false);
            redirectToAuth();
            return;
        }

        // Step 4: Token present, try to get session
        setUrlToken(tokenFromUrl);
        const loginF = async () => {
            if (requestedUrlTokenRef.current === tokenFromUrl) return;
            requestedUrlTokenRef.current = tokenFromUrl;
            try {
                const response = await getToken(tokenFromUrl);
                switch (response.error_code) {
                    case '02.04.00':
                        setAccessToken(response.data.token);
                        setBaseAPIToken(response.data.token);
                        setRefreshToken(response.data.refresh_token);
                        await loadAssociations();
                        break;
                    case '02.04.61':
                        redirectToAuth();
                        break;
                    default:
                        redirectToAuth();
                        break;
                }
            } catch (error) {
                console.error("Errore durante il login:", error);
                redirectToAuth();
            } finally {
                setLoadingStatus(false);
            }
        };
        loginF();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deviceUUID, isAuthenticated, validatingDevice, validatingSession, setLoadingStatus]);

    useEffect(() => {
        if (isReadyToLogin && isAuthenticated) {
            changeAssociation(association!);
        } else if (isReadyToLogin && association !== null) {
            login(accessToken, association, refreshToken);
        }
    }, [isReadyToLogin]);

    if (loading || validatingDevice || validatingSession || (isReadyToLogin && !isAuthenticated)) {
        // Use only global loading
        return null;
    }

    return (
        <div className="association-selection">
            {noAssociation === null ? (
                // No local loading, just empty or a spinner if you want
                <div></div>
            ) : noAssociation ? (
                <div className="no-association-container">
                    <h1 className="no-association-title">You are not part of any association yet</h1>
                    <p className="no-association-message">
                        We couldn't find any association linked to your account.
                        If you think this is an error, please check your inbox (including spam) for
                        any invitations or contact an administrator of your association.
                    </p>
                    <div className="no-association-suggestions">
                        <ul>
                            <li>Ensure the email address you used is correct.</li>
                            <li>Check your spam folder for any missed invitations.</li>
                            <li>Ask your association's administrator to send you a new invitation.</li>
                        </ul>
                    </div>
                </div>
            ) : (
                <>
                    <h1>Select Your Association</h1>
                    <div className="blob-container">
                        {associations.map((association) => (
                            <button
                                key={association.id}
                                type="button"
                                className="blob"
                                onClick={() => {
                                    setAssociation(association.id);
                                    setIsReadyToLogin(true);
                                }}
                            >
                                <img
                                    src={association.icon && association.icon.trim() !== '' ? association.icon : defaultAssociationIcon}
                                    alt={association.name}
                                    className="blob-icon"
                                />
                                <span className="blob-name">{association.name}</span>
                            </button>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default Login;
